import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  Drawer,
  Button,
  Tooltip,
  message,
  Tabs,
  Card,
  Modal,
  Form,
  Input,
  Rate,
  Radio,
  Table,
} from "antd";
import {
  DownOutlined,
  ArrowsAltOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  FileTextOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import Grade from "../../../components/Grade";

type GradedValue = {
  gradeTheReport: string;
  spellCheck: string;
  comments: string;
};

type CaseData = {
  headerText: string;
  imagePath: string;
  hisDocuments: {
    consultation: string;
    lab: string;
  };
  history: {
    "Patient History": string;
    "Clinical Notes": string;
    Medical: string;
  };
  activityLog: string[];
  findings: [string[], string[]];
  patientInfo?: {
    name: string;
    age: number;
    gender: "Male" | "Female" | "Other";
  };
};

// Example: Map your case IDs to JSON file names for dynamic report generation
const reportJsonMap: Record<string, [string, string]> = {
  "4": ["naveen", "naveen"], // Using naveen.json for both reports A and B
  "5": ["ragu", "ragu"], // Using ragu.json for both reports A and B
  "6": ["jivan_student", "jivan_student"], // Using jivan_student.json for both reports A and B
};

// Map case IDs to their corresponding image paths
const imagePathMap: Record<string, string> = {
  "4": "https://dcm.5cnetwork.com/view.html",
  "5": "https://dcm.5cnetwork.com/?token=dv9CiL9agIEpt2pSXe2bOqR3ST7XaZBJ9c8t7RoZUBBoSm8AVovEz1EMQ5eo-ge2tREfi-XEb5pNXY0ODQup8agKfLgIX15gmOt_jPcQ2JB_B0mxUCYjJs_CKQY=",
  "6": "https://dcm.5cnetwork.com/?token=dv9CiL9agIEpt2pSXe2bOqR3STnQaZdI9c0r5BUYURNuTG8AVovEz1FeH8D7qgTotRoa3rbEb54fX41VWAn-8aVcLbgNXA5hzep_iKuYRrrqaSSY28W-XKqQf4I=",
};

// Add AiGradeMap at the top of the file (after imports)
const AiGradeMap: { [key: string]: { Grade: string; Reason: string } } = {
  "4": {
    Grade: "2B",
    Reason:
      "The junior radiologist missed a small but clearly enhancing extra-axial lesion with a dural tail in the anterior interhemispheric cistern, consistent with a meningioma. While not large or causing mass effect, the lesion demonstrates characteristic imaging features that a competent radiologist should detect most of the time, making it a clinically significant but not egregious miss.",
  },
  "5": {
    Grade: "3B",
    Reason:
      "A clearly visible epidural hematoma with typical shape and location was missed despite trauma history and associated bone fracture and soft tissue swelling. This is an obvious, clinically significant abnormality that any competent radiologist should almost always detect.",
  },
  "6": {
    Grade: "3A",
    Reason:
      "Clinically insignificant but obvious adrenal adenoma that should almost always be detected and reported in a contrast-enhanced CT abdomen.",
  },
};

const findingsMap: { [key: string]: [string[], string[]] } = {
  "4": [
    ["Rathke's cleft cyst"],
    ["Anterior interhemispheric meningioma", "Rathke's cleft cyst"],
  ],
  "5": [
    ["Scalp hematoma"],
    [
      "Epidural hematoma (right parietal)",
      "Parietal bone fracture",
      " Scalp hematoma",
    ],
  ],
  "6": [
    [
      "Fatty liver",
      "Right renal calculi",
      "Left inguinal hernia",
      "Prostatomegaly",
    ],
    [
      "Fatty liver",
      "Right renal calculi",
      "Left inguinal hernia",
      "Prostatomegaly",
      "Left adrenal adenoma",
    ],
  ],
};

const HistorySectionContentMap: {
  [key: string]: {
    "Patient History": string;
    "Clinical Notes": string;
    "Medical": string;
  };
} = {
  "4": {
    "Patient History": "Presented for evaluation of headache and giddiness.",
    "Clinical Notes":
      " Patient reports onset of persistent headaches over the past few weeks, sometimes associated with giddiness. No reported history of trauma, fever, or seizures. Routine neurological examination was within normal limits at the time of assessment. Imaging requested to investigate potential underlying causes.",
    "Medical":
      "No significant past medical history of chronic illness reported. No known drug allergies.",
  },
  "5": {
    "Patient History": " Presented following a Road Traffic Accident (RTA).",
    "Clinical Notes":
      "Patient was involved in a motor vehicle collision and presented to the emergency department with concerns for head and facial trauma. Patient was conscious and oriented on arrival (e.g., GCS 15). Physical examination revealed superficial soft tissue injury in the right parietal region. Imaging performed to rule out intracranial injury or facial fractures.",
    "Medical":
      "No significant prior medical history or known comorbidities. No known drug allergies.",
  },
  "6": {
    "Patient History": " History of abdomen pain, vomiting, and chest pain.",
    "Clinical Notes":
      "Patient reports acute onset of severe upper abdominal pain, accompanied by multiple episodes of vomiting. Also reports some associated non-radiating chest discomfort. Physical examination revealed generalized abdominal tenderness, particularly in the upper quadrants. Evaluation initiated for acute abdominal pathology, considering the patient's age.",
    "Medical":
      " History of Hypertension. Potential age-related changes like early degenerative disease noted clinically or incidentally. No known drug allergies.",
  },
};

const ActivityLogContent: { [key: string]: string[] } = {
  "4": [
    "05-03-2025, 8:00 AM: Study ACTIVATED by System ",
    "05-03-2025, 8:05 AM: Study CREATED in Radiologist Pool ",
    "05-03-2025, 9:15 AM: Study ASSIGNED to Dr.JR by System Bot ",
    "05-03-2025, 9:16 AM: Study OPENED by Dr.JR",
    "05-03-2025, 9:35 AM: Study DRAFTED by Dr.JR",
    "05-03-2025, 10:30 AM: Study ASSIGNED to Dr. Satyanarayana by System Bot ",
    "05-03-2025, 10:33 AM: Study OPENED by Dr. Satyanarayana ",
    "05-03-2025, 10:45 AM: Study REPORTED by Dr. Satyanarayana ",
    "05-03-2025, 11:00 AM: Study COMPLETED",
  ],
  "5": [
    "05-01-2025, 9:00 AM: Study ACTIVATED by System",
    "05-01-2025, 9:04 AM: Study CREATED in Radiologist Pool ",
    "05-01-2025, 9:50 AM: Study ASSIGNED to Dr. Prashanth V by System Bot ",
    "05-01-2025, 9:51 AM: Study OPENED by Dr. Prashanth V ",
    "05-01-2025, 10:10 AM: Study REPORTED by Dr. Prashanth V ",
    "05-01-2025, 11:00 AM: Report CT BRAIN & FACE PLAIN Disapproved by DR UDHAYA - Second Opinion Requested ",
    "05-01-2025, 11:01 AM: Study ACTIVATED ",
    "05-01-2025, 11:02 AM: Study ASSIGNED to Dr. Mohammad Faraz Khan Mohammad Farid Khan by System Bot ",
    "05-01-2025, 11:10 AM: Study OPENED by Dr. Mohammad Faraz Khan Mohammad Farid Khan ",
    "05-01-2025, 11:35 AM: Study REPORTED (SECOND OPINION) by Dr. Mohammad Faraz Khan Mohammad Farid Khan",
    "05-01-2025, 12:00 PM: Study COMPLETED",
  ],
  "6": [
    "05-02-2025, 8:30 AM: Study ACTIVATED by System",
    "05-02-2025, 8:35 AM: Study CREATED in Radiologist Pool ",
    "05-02-2025, 9:20 AM: Study ASSIGNED to Dr. Shashank G H by System Bot ",
    "05-02-2025, 9:21 AM: Study OPENED by Dr. Shashank G H ",
    "05-02-2025, 9:55 AM: Study REPORTED by Dr. Shashank G H ",
    "05-02-2025, 10:45 AM: Report: CT ABDOMEN & PELVIS PLAIN AND CONTRAST Disapproved by Referring Physician from CIVIL HOSPITAL MORBI",
    "05-02-2025, 10:46 AM: Study Rework Created - Second Opinion Requested",
    "05-02-2025, 10:46 AM: Study ACTIVATED ",
    "05-02-2025, 10:47 AM: Study ASSIGNED to Dr. Mohammad Faraz Khan Mohammad Farid Khan by System Bot ",
    "05-02-2025, 10:55 AM: Study OPENED by Dr. Mohammad Faraz Khan Mohammad Farid Khan ",
    "05-02-2025, 11:20 AM: Study REPORTED (SECOND OPINION) by Dr. Mohammad Faraz Khan Mohammad Farid Khan ",
    "05-02-2025, 11:45 AM: Study COMPLETED",
  ],
};

function ReportViewer({ jsonFileName, reportType }: { jsonFileName: string; reportType: 'reportA' | 'reportB' }) {
  const [html, setHtml] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!jsonFileName) return;
    
    // Load the report template
    fetch('/report_template.html')
      .then((res) => res.text())
      .then((templateHtml) => {
        // Create a blob URL for the template with the JSON parameter and report type
        const url = `/report_template.html?report=${jsonFileName}&type=${reportType}`;
        setHtml(`<iframe src="${url}" style="width:100%;height:100%;border:none;"></iframe>`);
      })
      .catch((err) => {
        setError(`Error loading template: ${err.message}`);
        setHtml("");
      });
  }, [jsonFileName, reportType]);

  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!html) return <div>Loading content...</div>;

  return (
    <div
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ height: "100%", overflow: "auto", padding: "20px" }}
    />
  );
}

const consultationColumns = [
  { title: "DATE", dataIndex: "date", key: "date" },
  { title: "REFERRING PHY", dataIndex: "referringPhy", key: "referringPhy" },
  { title: "DEPARTMENT", dataIndex: "department", key: "department" },
  {
    title: "DOCUMENTS",
    dataIndex: "documentUrl",
    key: "documentUrl",
    render: (url: string) =>
      url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#6c63ff" }}
          onClick={(e) => {
            e.preventDefault();
            const newWindow = window.open(url, '_blank');
            if (newWindow) {
              newWindow.document.title = 'RIS QA';
            }
          }}
        >
          View
        </a>
      ) : (
        <span style={{ color: "#aaa" }}>No document</span>
      ),
  },
];

const labColumns = [
  { title: "DATE", dataIndex: "date", key: "date" },
  { title: "TEST NAME", dataIndex: "testName", key: "testName" },
  { title: "REFERRING PHY", dataIndex: "referringPhy", key: "referringPhy" },
  { title: "DEPARTMENT", dataIndex: "department", key: "department" },
  {
    title: "DOCUMENTS",
    dataIndex: "documentUrl",
    key: "documentUrl",
    render: (url: string) =>
      url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#6c63ff" }}
          onClick={(e) => {
            e.preventDefault();
            const newWindow = window.open(url, '_blank');
            if (newWindow) {
              newWindow.document.title = 'RIS QA';
            }
          }}
        >
          View
        </a>
      ) : (
        <span style={{ color: "#aaa" }}>No document</span>
      ),
  },
];

const actionColors = {
  ACTIVATED: "#4CAF50",
  CREATED: "#2196F3",
  ASSIGNED: "#FF9800",
  OPENED: "#9C27B0",
  DRAFTED: "rgb(255, 14, 122)",
  REPORTED: "#ff0000",
  COMPLETED: "#4CAF50",
} as const;

type ActionType = keyof typeof actionColors;

const AIGradeCard = styled.div`
  background: hsl(0, 0%, 100%);
  padding: 0.6rem;
  border-radius: 8px;
  margin-top: 0.3rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.8rem;
  font-size: 0.75rem;
  z-index: ;
  transition: all 0.3s ease;
  max-height: 3.5rem; /* Reduced height */
  overflow: hidden;

  &::after {
    position: absolute;
    content: "";
    top:-5
    left: 0;
    right: 0;
    z-index: -1;
    height: 80%;
    width: 100%;
    opacity: 0.5;
    filter: blur(10px);
    background: linear-gradient(
      45deg,
      #ff5770,
      #e4428d,
      #c42da8,
      #9e16c3,
      #6501de,
      #9e16c3,
      #c42da8,
      #e4428d,
      #ff5770
    );
    background-size: 200% 200%;
    animation: animateGlow 2s ease-in-out infinite;
    opacity: 0.7;
    border-radius: 8px;
  }

  @keyframes animateGlow {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

const AIGradeText = styled.div`
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  font-family: "Google Sans", "Segoe UI", "Roboto", sans-serif;
  font-size: 0.8rem;
  color: transparent;
  background: linear-gradient(
    90deg,
    #4285f4,
    #9b72cb,
    #d96570,
    #f4b400,
    #4285f4
  );
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  animation: shine 8s ease-in-out infinite;

  @keyframes shine {
    to {
      background-position: 200% center;
    }
  }
`;

const AIReasonText = styled.div`
  color: #595959;
  line-height: 1.4;
  font-family: "Segoe UI", "Roboto", sans-serif;
  flex: 1;
  max-height: 1.8rem; /* Reduced to 2 lines with smaller line height */
  overflow-y: auto;
  overflow-x: hidden;
  font-size: 0.7rem;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  strong {
    font-weight: 600;
    color: #333;
  }
`;

const QAGradeCard = styled.div`
  background: #f9f9f9;
  padding: 1rem;
  border-radius: 12px;
  margin-top: 0.5rem;
  width: 96%;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 1.2rem;
  font-size: 0.8rem;
`;

const PatientInfoContainer = styled.div`
  padding: 24px 24px 16px 24px;
`;

const PatientName = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const PatientDetails = styled.p`
  font-size: 1rem;
  color: #595959;
  margin: 4px 0 0;
`;

const DocumentImage = styled.img`
  height: 200px;
  width: auto;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  margin-top: 16px;
  cursor: pointer;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const parseDateFromLog = (logString: string): Date => {
  // Tries to parse dates like "05-03-2025, 8:00 AM" at the start of the string
  let match = logString.match(
    /^(\d{2}-\d{2}-\d{4}), (\d{1,2})[ :]+(\d{2}) (AM|PM)/
  );
  if (match) {
    const [, datePart, hoursStr, minutesStr, ampm] = match;
    const [day, month, year] = datePart.split("-").map(Number);
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (ampm === "PM" && hours < 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;

    return new Date(year, month - 1, day, hours, minutes);
  }

  return new Date(0); // Fallback for unparsed dates
};

const parseLogEntryForDisplay = (
  logString: string
): { timestamp: string; message: string } => {
  const match = logString.match(
    /^((?:\d{2}-\d{2}-\d{4}), (?:\d{1,2})[ :]+(?:\d{2}) (?:AM|PM)): (.*)$/
  );

  if (match) {
    return { timestamp: match[1], message: match[2].trim() };
  }

  return { timestamp: "", message: logString };
};

const StudentReport = () => {
  const { id } = useParams<{ id: string }>();

  const [leftJsonFileName, rightJsonFileName] = reportJsonMap[id ?? ""] || ["", ""];
  const [caseData, setCaseData] = useState<CaseData | null>(null);

  //React States
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);
  const [isPatientHistoryDrawerVisible, setIsPatientHistoryDrawerVisible] =
    useState(false);
  const [isGradedInLeftSide, setIsGradedInLeftSide] = useState(false);
  const [isGradedInRightSide, setIsGradedInRightSide] = useState(false);
  const [gradedValueInLeftSide, setGradedValueInLeftSide] =
    useState<GradedValue>({ gradeTheReport: "", spellCheck: "", comments: "" });
  const [gradedValueInRightSide, setGradedValueInRightSide] =
    useState<GradedValue>({ gradeTheReport: "", spellCheck: "", comments: "" });
  const [aiGradingData, setAiGradingData] = useState<{ grade: string; reason: string } | null>(null);
    
  const [form] = Form.useForm();

  // Load case data and AI grading data
  useEffect(() => {
    if (id) {
      const savedGradeA = localStorage.getItem(`student_qa_grade_details_A_${id}`);
      if (savedGradeA) {
        setGradedValueInLeftSide(JSON.parse(savedGradeA));
        setIsGradedInLeftSide(true);
      }
      const savedGradeB = localStorage.getItem(`student_qa_grade_details_B_${id}`);
      if (savedGradeB) {
        setGradedValueInRightSide(JSON.parse(savedGradeB));
        setIsGradedInRightSide(true);
      }
    }

    if (leftJsonFileName) {
      fetch(`/report_data/${leftJsonFileName}.json`)
        .then((res) => res.json())
        .then((data) => {
          if (data.aiGrading) {
            setAiGradingData(data.aiGrading);
          }
          if (data.qaGradingA && !localStorage.getItem(`student_qa_grade_details_A_${id}`)) {
            setGradedValueInLeftSide(data.qaGradingA);
            setIsGradedInLeftSide(true);
          }
          if (data.qaGrading && !localStorage.getItem(`student_qa_grade_details_B_${id}`)) {
            setGradedValueInRightSide(data.qaGrading);
            setIsGradedInRightSide(true);
          }
          if (data.caseData) {
            setCaseData(data.caseData);
          }
        })
        .catch((err) => {
          console.error('Error loading case data:', err);
        });
    }
  }, [id, leftJsonFileName]);

  const consultationDataUrl = caseData?.hisDocuments?.consultation || "";
  const labDataUrl = caseData?.hisDocuments?.lab || "";

  const hisDocumentsSectionData = {
    consultations: [
      {
        key: "1",
        date: "15 Mar 2021",
        referringPhy: "Dr. Kiran",
        department: "Neurology",
        documentUrl: consultationDataUrl,
      },
    ],
    labs: [
      {
        key: "1",
        date: "15 Mar 2021",
        testName: "Radiology Report",
        referringPhy: "Dr. Kiran",
        department: "Neurology",
        documentUrl: labDataUrl,
      },
    ],
  };

  const handleLeftGradeSubmit = (values: any) => {
    message.success("Report A graded successfully!");
    const newGradeA = {
      gradeTheReport: values["Grade the report"],
      spellCheck: values["spellCheck"],
      comments: values["comments"],
    };
    setGradedValueInLeftSide(newGradeA);
    setIsGradedInLeftSide(true);
    if (id) {
      localStorage.setItem(`student_qa_grade_details_A_${id}`, JSON.stringify(newGradeA));
    }

    if (!isGradedInRightSide) {
      const defaultGradeB = {
        gradeTheReport: "1A: Concur with interpretation",
        spellCheck: "A",
        comments: "Auto-concurred on Report A submission.",
      };
      setGradedValueInRightSide(defaultGradeB);
      setIsGradedInRightSide(true);
      if (id) {
        localStorage.setItem(`student_qa_grade_details_B_${id}`, JSON.stringify(defaultGradeB));
        localStorage.setItem(`grade_${id}`, defaultGradeB.gradeTheReport);
      }
    }
    form.resetFields();
  };

  const handleRightGradeSubmit = (values: any) => {
    message.success("Report B graded successfully!");
    const newGradeB = {
      gradeTheReport: values["Grade the report"],
      spellCheck: values["spellCheck"],
      comments: values["comments"],
    };
    setGradedValueInRightSide(newGradeB);
    setIsGradedInRightSide(true);
    if (id) {
      localStorage.setItem(`grade_${id}`, newGradeB.gradeTheReport);
      localStorage.setItem(`student_qa_grade_details_B_${id}`, JSON.stringify(newGradeB));
    }

    if (!isGradedInLeftSide) {
      const defaultGradeA = {
        gradeTheReport: "1A: Concur with interpretation",
        spellCheck: "A",
        comments: "Auto-concurred on Report B submission.",
      };
      setGradedValueInLeftSide(defaultGradeA);
      setIsGradedInLeftSide(true);
      if (id) {
        localStorage.setItem(`student_qa_grade_details_A_${id}`, JSON.stringify(defaultGradeA));
      }
    }
    form.resetFields();
  };

  const onDrawerClose = () => {
    setIsPatientHistoryDrawerVisible(false);
  };

  const handleImageFooterClick = () => {
    if (!caseData?.imagePath) {
      message.error("No image available for this case");
      return;
    }
    window.open(caseData.imagePath, "_blank");
  };

  const handlePatientHistoryFooterClick = () => {
    setIsPatientHistoryDrawerVisible(true);
  };

  const handleSubmit = () => {
    setIsSubmitButtonClicked(true);
    message.success("Student report submitted successfully! This tab will now close.");
    setTimeout(() => {
      if (window.opener) {
        window.opener.location.reload();
      }
      window.close();
    }, 1500);
  };

  if (!caseData) {
    return <div>Loading case data...</div>;
  }

  return (
    <MainContainer>
      <style>{`html, body { overflow: hidden !important; }`}</style>
      <PageHeaderContainer>
        <PageHeaderLeft>
          <PageHeader>{caseData.headerText}</PageHeader>
          <SubHeader>
            <a
              href="#"
              style={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                color: " #9370DB",
                gap: "0.3rem",
                fontSize: "0.7rem",
              }}
            >
              Open in Pacs Viewer
              <ViewInNewTabButtonIcon
                src={"/icons/open-in-new-tab-white.svg"}
              />
            </a>
          </SubHeader>
        </PageHeaderLeft>
        <SubmitButton 
          onClick={handleSubmit}
          disabled={!isGradedInLeftSide || !isGradedInRightSide}
        >
          {isSubmitButtonClicked ? "Updated" : "Submit"}
        </SubmitButton>
      </PageHeaderContainer>
      <SplitScreenContainer>
        <PanelContainer>
          <PanelHeader>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                flexDirection: "row",
              }}
            >
              <span style={{ whiteSpace: "nowrap" }}>Report A</span>
              <Grade
                onGradeSubmit={handleLeftGradeSubmit}
                reportId={id}
              />
            </div>

            {isGradedInLeftSide ? (
              <QAGradeCard>
                <div
                  style={{
                    color: "#333",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {`QA Grade : ${gradedValueInLeftSide.gradeTheReport} (${gradedValueInLeftSide.spellCheck})`}
                </div>
                {gradedValueInLeftSide.comments && (
                  <AIReasonText>
                    <strong>Reason - </strong>
                    {gradedValueInLeftSide.comments}
                  </AIReasonText>
                )}
              </QAGradeCard>
            ) : (
              aiGradingData && !isGradedInRightSide && (
                <AIGradeCard>
                  <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <AIGradeText>
                      {`AI Grade : ${aiGradingData.grade}`}
                    </AIGradeText>
                    <img src="/icons/ai.svg" alt="AI Icon" style={{ width: '15px', height: '15px', marginLeft: '8px' }} />
                  </div>
                  {aiGradingData.reason && (
                    <AIReasonText>
                      <strong>Reason - </strong>
                      {aiGradingData.reason}
                    </AIReasonText>
                  )}
                </AIGradeCard>
              )
            )}

            {caseData.findings && caseData.findings.length > 0 && (
              <div style={{ marginTop: '0.2rem' }}>
                <div style={{ 
                  fontSize: '0.75rem', 
                  fontWeight: '600', 
                  color: '#666', 
                  marginBottom: '0.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.15rem' }}>
                  {caseData.findings[0].map((finding, index) => (
                    <FindingsContent key={index}>{finding}</FindingsContent>
                  ))}
                </div>
              </div>
            )}
          </PanelHeader>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <ReportViewer jsonFileName={leftJsonFileName} reportType="reportA" />
          </div>
          <PatientHistoryViewer $isOpen={isPatientHistoryDrawerVisible}>
            <h3>{}</h3>
            <PatientHistoryCloseBtn onClick={onDrawerClose}>
              &times;
            </PatientHistoryCloseBtn>
            
            {caseData.patientInfo && (
              <PatientInfoContainer>
                <PatientName>{caseData.patientInfo.name}</PatientName>
                <PatientDetails>
                  {caseData.patientInfo.age} | {caseData.patientInfo.gender}
                </PatientDetails>
              </PatientInfoContainer>
            )}
            <PatientHistoryContent>
              <Tabs
                defaultActiveKey="1"
                items={[
                  {
                    key: "1",
                    label: "History",
                    children: (
                      <HistorySection>
                        <HistoryItem>
                          <HistoryLabel>Patient History</HistoryLabel>
                          <HistoryContent>
                            {caseData.history["Patient History"]}
                          </HistoryContent>
                        </HistoryItem>
                        <HistoryItem>
                          <HistoryLabel>Clinical Notes</HistoryLabel>
                          <HistoryContent>
                            {caseData.history["Clinical Notes"]}
                          </HistoryContent>
                        </HistoryItem>
                        <HistoryItem>
                          <HistoryLabel>Medical</HistoryLabel>
                          <HistoryContent>
                            {caseData.history["Medical"]}
                          </HistoryContent>
                        </HistoryItem>
                        
                      </HistorySection>
                    ),
                  },
                  {
                    key: "2",
                    label: "Past Procedures",
                    children: (
                      <div style={{ padding: "1rem" }}>
                        <p>No past procedures available for this case.</p>
                      </div>
                    ),
                  },
                  {
                    key: "3",
                    label: "HIS Documents",
                    children: (
                      <DocumentList>
                        
                        <h3 style={{ marginBottom: "1rem" }}>
                          Previous Consultations
                        </h3>
                        {hisDocumentsSectionData.consultations.map((item) => (
                          <DocumentItem key={item.key}>
                            <DocumentIcon>
                              <FileTextOutlined />
                            </DocumentIcon>
                            <DocumentInfo>
                              <DocumentTitle>
                                {item.department} Consultation
                              </DocumentTitle>
                              <DocumentMeta>
                                {item.date} - {item.referringPhy}
                              </DocumentMeta>
                            </DocumentInfo>
                            {item.documentUrl && (
                              <DocumentLink
                                href={item.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </DocumentLink>
                            )}
                          </DocumentItem>
                        ))}

                        <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>
                          Previous Lab Results
                        </h3>
                        {hisDocumentsSectionData.labs.map((item) => (
                          <DocumentItem key={item.key}>
                            <DocumentIcon>
                            <FileTextOutlined />
                            </DocumentIcon>
                            <DocumentInfo>
                              <DocumentTitle>{item.testName}</DocumentTitle>
                              <DocumentMeta>
                                {item.date} - {item.department}
                              </DocumentMeta>
                            </DocumentInfo>
                            {item.documentUrl && (
                              <DocumentLink
                                href={item.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </DocumentLink>
                            )}
                          </DocumentItem>
                        ))}
                      </DocumentList>
                    ),
                  },
                  {
                    key: "4",
                    label: "Activity Log",
                    children: (
                      <ActivityLogContainer>
                        {[...caseData.activityLog]
                          .sort(
                            (a, b) =>
                              parseDateFromLog(b).getTime() -
                              parseDateFromLog(a).getTime()
                          )
                          .map((entry, index) => {
                            const { timestamp, message: fullMessage } =
                              parseLogEntryForDisplay(entry);

                            let message = fullMessage;
                            const details: { [key: string]: string } = {};

                            const detailKeys = ["Comment", "Reason"];
                            detailKeys.forEach((key) => {
                              if (message.includes(`${key}:`)) {
                                const parts = message.split(`${key}:`);
                                message = parts[0].trim();
                                details[key] = parts[1].trim();
                              }
                            });

                            let action: string | undefined;
                            let color: string = "#333";
                            let beforeAction: string;
                            let afterAction: string = "";
                            const knownActions = Object.keys(actionColors);

                            const foundAction = knownActions.find((act) =>
                              message.includes(` ${act} `)
                            );

                            if (foundAction) {
                              action = foundAction;
                              color =
                                actionColors[
                                  action as keyof typeof actionColors
                                ];
                              const parts = message.split(action);
                              beforeAction = parts[0];
                              afterAction = parts[1];
                            } else {
                              beforeAction = message;
                            }

                            return (
                              <ActivityLogItem key={index}>
                                <ActivityLogMessage>
                                  <div>
                                    {beforeAction}
                                    {action && (
                                      <ActionText color={color}>
                                        {action}
                                      </ActionText>
                                    )}
                                    {afterAction}
                                  </div>
                                  {details["Reason"] && (
                                    <ActivityDetail>
                                      <strong>Reason:</strong>{" "}
                                      {details["Reason"]}
                                    </ActivityDetail>
                                  )}
                                  {details["Comment"] && (
                                    <ActivityDetail>
                                      <strong>Comment:</strong>{" "}
                                      {details["Comment"]}
                                    </ActivityDetail>
                                  )}
                                </ActivityLogMessage>
                                <ActivityLogTimestamp>
                                  {timestamp}
                                </ActivityLogTimestamp>
                              </ActivityLogItem>
                            );
                          })}
                      </ActivityLogContainer>
                    ),
                  },
                ]}
              />
            </PatientHistoryContent>
          </PatientHistoryViewer>
        </PanelContainer>
        <Divider />
        <PanelContainer>
          <PanelHeader
            style={{ flexDirection: "column", alignItems: "stretch" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                flexDirection: "row",
              }}
            >
              <span style={{ whiteSpace: "nowrap" }}>Report B</span>
              <Grade
                onGradeSubmit={handleRightGradeSubmit}
                reportId={id}
              />
            </div>

            {isGradedInRightSide && (
              <QAGradeCard>
                <div
                  style={{
                    color: "#333",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    marginRight: "1.2rem",
                    flexShrink: 0,
                  }}
                >
                  {`QA Grade :${gradedValueInRightSide.gradeTheReport} (${gradedValueInRightSide.spellCheck})`}
                </div>
                <div
                  style={{
                    color: "#666",
                    lineHeight: 1.4,
                    flex: 1,
                    margin: 0,
                    fontSize: "0.8rem",
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                >
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <span>
                      {gradedValueInRightSide.comments && (
                        <>
                          <span
                            style={{ fontWeight: 500, whiteSpace: "nowrap" }}
                          >
                            Reason -
                          </span>
                          {gradedValueInRightSide.comments}
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </QAGradeCard>
            )}

            {caseData.findings && caseData.findings.length > 0 && (
              <div style={{ marginTop: '0.2rem' }}>
                <div style={{ 
                  fontSize: '0.75rem', 
                  fontWeight: '600', 
                  color: '#666', 
                  marginBottom: '0.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.15rem' }}>
                  {caseData.findings[1].map((finding, index) => (
                    <FindingsContent key={index}>{finding}</FindingsContent>
                  ))}
                </div>
              </div>
            )}
          </PanelHeader>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <ReportViewer jsonFileName={rightJsonFileName} reportType="reportB" />
          </div>
        </PanelContainer>
      </SplitScreenContainer>
      <Footer>
        <Tooltip title="Click to view image">
          <CustomeButton onClick={handleImageFooterClick}>
            Image View <DownOutlined style={{ marginLeft: "8px" }} />
          </CustomeButton>
        </Tooltip>
        <Tooltip title="Click to view Patient History" style={{ gap: "1rem" }}>
          <CustomeButton onClick={handlePatientHistoryFooterClick}>
            Patient History <ArrowsAltOutlined style={{ marginLeft: "8px" }} />
          </CustomeButton>
        </Tooltip>
      </Footer>
    </MainContainer>
  );
};

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  overflow: hidden;
`;

const SplitScreenContainer = styled.div`
  display: flex;
  flex: 1 1 0;
  height: calc(100vh - 120px);
  width: 100%;
  overflow: hidden;
  background: #fff;
  gap: 1rem;
  padding: 1rem;
  box-sizing: border-box;
`;

const PanelContainer = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: #fff;
  position: relative;
  padding: 0;
  margin: 0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
`;

const PanelHeader = styled.div`
  flex-shrink: 0;
  padding: 1rem;
  background: rgba(216, 212, 240, 0.5);
  border-bottom: 1px solid #ddd;
  z-index: 1;
  font-weight: 500;
  font-size: 1.1rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  flexDirection: column,
  alignItems: stretch 
`;

const Divider = styled.div`
  width: 8px;
  background: #000;
  flex-shrink: 0;
`;

const PageHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: #f5f5f5;
  padding: 0.5rem 0.5rem 0.5rem 0.5rem;
`;

const PageHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.h1`
  font-size: 1rem;
  font-weight: bold;
  margin: 0;
  color: #222;
  background: #f5f5f5;
`;

const SubHeader = styled.div`
  font-size: 1rem;
  color: #007bff;
`;

const SubmitButton = styled.button`
  background: ${props => props.disabled ? '#ccc' : 'rgb(87, 61, 236)'};
  color: #fff;
  border: none;
  border-radius: 2rem;
  padding: 0.5rem 2rem 0.5rem 2rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-bottom: 0.5rem;
  align-self: flex-start;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: background 0.2s;

  &:hover {
    background: ${props => props.disabled ? '#ccc' : '#c2bbec'};
  }
`;

const ViewInNewTabButtonIcon = styled.img`
  height: 1rem;
  width: auto;
  color: #9370db;
`;

const Footer = styled.footer`
  background: rgb(47, 47, 46);
  color: rgb(20, 142, 71);
  text-align: left;
  padding: 1rem 2rem;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CustomeButton = styled(Button)`
  background: rgb(71, 70, 73);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  &:hover {
    background: rgb(90, 89, 92);
    color: white;
  }
`;

const FindingsContent = styled.div`
  display: inline-block;
  font-size: 0.65rem;
  color: #d9534f;
  background-color: #fdf2f2;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  white-space: nowrap;
  margin: 0.1rem;
  font-weight: 500;
  border: 1px solid #f8d7da;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #f8d7da;
    color: #c9302c;
  }
`;

const PatientHistoryViewer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  bottom: -5px;
  left: -5px;
  width: 99%;
  height: 99%;
  background: white;
  margin-left: -12px;
  margin-bottom: -12px;
  z-index: 100;
  transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);

  border-top-right-radius: 30px;
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

const PatientHistoryCloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  color: #888;
  position: absolute;
  top: 20px;
  right: 24px;
  z-index: 101;
  &:hover {
    color: #333;
  }
`;

const PatientHistoryContent = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 0;

  .ant-tabs-nav {
    padding: 0 24px;
    margin-bottom: 0;
  }

  .ant-tabs-content-holder {
    padding: 24px;
  }
`;

const HistorySection = styled.div``;

const HistoryItem = styled.div`
  margin-bottom: 1.5rem;
`;

const HistoryLabel = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const HistoryContent = styled.p`
  font-size: 0.9rem;
  color: #555;
  margin: 0;
  line-height: 1.7;
`;

const DocumentList = styled.div``;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fcfcfc;
  border: 1px solid #e8e8e8;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #d9d9d9;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }
`;

const DocumentIcon = styled.div`
  margin-right: 12px;
  font-size: 1.5rem;
  color: #6c63ff;
`;

const DocumentInfo = styled.div`
  flex: 1;
`;

const DocumentTitle = styled.div`
  font-weight: 600;
`;

const DocumentMeta = styled.div`
  font-size: 0.85rem;
  color: #888;
`;

const DocumentLink = styled.a`
  color: #6c63ff;
  font-weight: 600;
  font-size: 0.9rem;
`;

const ActivityLogContainer = styled.div``;

const ActivityLogItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 0;
  font-size: 0.9rem;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const ActivityLogMessage = styled.div`
  flex: 1;
  padding-right: 1rem;
  line-height: 1.5;
`;

const ActivityLogTimestamp = styled.div`
  color: #888;
  white-space: nowrap;
  font-size: 0.85rem;
`;

const ActionText = styled.span<{ color: string }>`
  color: ${(props) => props.color};
  font-weight: 600;
`;

const ActivityDetail = styled.div`
  margin-top: 0.5rem;
  color: #555;
  font-size: 0.85rem;
  padding-left: 1rem;
  border-left: 2px solid #eee;
  margin-left: 4px;
`;

export default StudentReport;
